import { useDispatch } from 'react-redux';
import {
    useLazyGetCartQuery,
    useRemoveFromCartMutation,
    useRemoveSavedSearchFromCartMutation,
    useRemoveBundleFromCartMutation,
} from '../../Api/Api';
import { clearSelectedCart } from '../../Store/CartSlice';
import { useHasGeneralStorefrontAccess } from '../Auth/useStorefrontAccess';

/**
 * Hook for the "start a new cart" action.
 *
 * Returns:
 *  - `startNewCart`: deselects any prepared cart and empties the session cart so
 *    leftovers from a previous, unordered cart don't carry over into the new one.
 *    The backend has no bulk "clear cart" endpoint, so we remove each item via the
 *    existing per-item delete mutations (same approach as CartPage.removeProducts).
 *  - `canStartNewCart`: whether the current user is allowed to start a new cart.
 *    Users granted access only to a specific prepared cart cannot — the button
 *    should be hidden, and `startNewCart` is a no-op for them so their prepared
 *    cart id is never cleared from local state.
 */
export function useStartNewCart() {
    const dispatch = useDispatch();
    const canStartNewCart = useHasGeneralStorefrontAccess();
    const [fetchCart] = useLazyGetCartQuery();
    const [removeFromCart] = useRemoveFromCartMutation();
    const [removeSavedSearchFromCart] = useRemoveSavedSearchFromCartMutation();
    const [removeBundleFromCart] = useRemoveBundleFromCartMutation();

    const startNewCart = async () => {
        // Cart-only users must keep their prepared cart selection — do nothing.
        if (!canStartNewCart) return;

        // Deselect any prepared cart.
        dispatch(clearSelectedCart());

        // Empty any leftover session cart items.
        try {
            const cart = await fetchCart().unwrap();
            for (const product of cart.products ?? []) {
                if (product.savedSearchId) {
                    await removeSavedSearchFromCart({ savedSearchId: product.savedSearchId, source: 'cart' });
                } else {
                    await removeFromCart({ productId: product.productId, source: 'cart' });
                }
            }
            for (const bundle of cart.bundles ?? []) {
                await removeBundleFromCart({ cartItemId: bundle.cartItemId ?? bundle.bundleId, source: 'cart' });
            }
        } catch {
            // Nothing to clear (or the cart failed to load) — start fresh anyway.
        }
    };

    return { startNewCart, canStartNewCart };
}
